import * as React from "react";
import { useForm } from "react-yup";
import * as Yup from "yup";

import {
  Alert,
  AlertProps,
  Form,
  ReportOpportunityForm as ReportOpp,
  SectionToggler,
  useSectionToggler,
  yupLocale,
} from "@skv/core";
import { supportDetails } from "@skv/util";

type ReportOpportunityFormModel = {
  opportunityId: number;
  email: string;
  reason: string;
  comments: string;
  supportDetails: string;
};

type FormErrors = {
  [key in keyof ReportOpportunityFormModel]?: string[];
};

const SCHEMA = Yup.object({
  opportunityId: Yup.number().required(),
  email: Yup.string().email().required().label("Email").nullable(),
  reason: Yup.string().required().label("Reason").nullable(),
  comments: Yup.string().required().label("Comments").nullable(),
  supportDetails: Yup.string().nullable().notRequired(),
}).defined();

export const ReportOpportunity = (props: ReportOpp) => {
  const [isSubmitSuccessful, setIsSubmitSuccessful] = React.useState(false);

  if (isSubmitSuccessful) {
    return (
      <Alert tabIndex={-1} show variant="success">
        Thank you. Your report has been forwarded to our support team.
      </Alert>
    );
  }

  return (
    <SectionToggler>
      <SectionToggler.Toggler title="Report this opportunity" />
      <SectionToggler.Content>
        <ReportOpportunityForm
          {...props}
          onSuccess={setIsSubmitSuccessful}
          supportDetails={JSON.stringify(supportDetails())}
        />
      </SectionToggler.Content>
    </SectionToggler>
  );
};

interface ReportOpportunityFormProps extends ReportOpp {
  onSuccess: React.Dispatch<React.SetStateAction<boolean>>;
}

const ReportOpportunityForm = ({
  onSuccess,
  reasons,
  ...defaultValues
}: ReportOpportunityFormProps) => {
  Yup.setLocale(yupLocale);
  const { toggleCollapse } = useSectionToggler();
  const alertRef = React.useRef<HTMLDivElement>(null);
  const [alertState, setAlertState] = React.useState<AlertProps>({
    show: false,
    variant: "danger",
    children: null,
  });

  const form = useForm({
    validationSchema: SCHEMA,
    defaultValues,
  });

  const handleSubmit = React.useMemo(() => {
    return form.createSubmitHandler(async (values) => {
      setAlertState((prev) => ({ ...prev, show: false }));

      await fetch("/api/report", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      })
        .then((response) => {
          if (response.ok) return response.json();
          throw Error("Unhandled network error");
        })
        .then(({ exception, success }) => {
          if (success) {
            onSuccess(true);
            return;
          }

          if (
            exception?.message ===
            "One or more validation failures have occurred."
          ) {
            setAlertState({
              show: true,
              variant: "danger",
              children: (
                <>
                  <p>Please fix the below errors in your form</p>
                  <ul className="mb-0">
                    {Object.entries(exception.failures as FormErrors)
                      .reduce((acc, cur) => {
                        acc = [...acc, ...cur[1]];
                        return acc;
                      }, [])
                      .map((error, idx) => (
                        <li key={idx}>{error}</li>
                      ))}
                  </ul>
                </>
              ),
            });

            alertRef.current.focus();
            return;
          }

          throw Error("Unhandled error");
        })
        .catch(() => {
          setAlertState({
            show: true,
            variant: "danger",
            children: (
              <>
                We could not report this opportunity at this time. Please try
                again later.
              </>
            ),
          });
          alertRef.current.focus();
          form.setSubmitting(false);
        });
    });
  }, [form, onSuccess]);

  return (
    <form.FormProvider>
      <div className="row">
        <div className="col-md-8">
          <section className="mb-6">
            <Alert tabIndex={-1} ref={alertRef} {...alertState} />

            <p>All fields marked with * are required</p>
            <form method="POST" onSubmit={handleSubmit}>
              <input
                type="hidden"
                id="opportunityId"
                name="opportunityId"
                value={defaultValues.opportunityId}
              />
              <input
                type="hidden"
                id="supportDetails"
                name="supportDetails"
                value={defaultValues.supportDetails}
              />

              <Form.Field
                type="email"
                id="email"
                name="email"
                label="Your email address"
                autoComplete="email"
                value={(form.getValue("email") as string) || ""}
                {...form.field}
              />

              <Form.Select
                id="reason"
                name="reason"
                label="Reason for reporting opportunity"
                value={(form.getValue("reason") as string) || ""}
                {...form.field}
              >
                <option value="">Select a reason</option>
                {reasons.map((reason) => (
                  <option key={reason.id} value={reason.name}>
                    {reason.name}
                  </option>
                ))}
              </Form.Select>

              <Form.TextArea
                id="comments"
                name="comments"
                label="Additional comments"
                rows={5}
                value={(form.getValue("comments") as string) || ""}
                {...form.field}
              />

              <button
                className="btn btn-secondary"
                disabled={form.isSubmitting}
              >
                {form.isSubmitting ? "Sending..." : "Report opportunity"}
              </button>

              <button
                type="button"
                className="btn btn-link ml-2"
                disabled={form.isSubmitting}
                onClick={toggleCollapse}
              >
                Cancel
              </button>

              <div
                css={`
                  position: absolute;
                  opacity: 0;
                  top: 0;
                  z-index: -1;
                `}
              >
                <Form.Field
                  tabIndex={0}
                  id="secondName"
                  name="secondName"
                  label="secondName"
                  autoComplete="off"
                  value={(form.getValue("secondName") as string) || null}
                  {...form.field}
                />
              </div>
            </form>
          </section>
        </div>
      </div>
    </form.FormProvider>
  );
};
